import { useState, useCallback } from "react"
import Cookies from "js-cookie"

const API = `${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/branding`

function authHeader() {
    // Auth token lives in the `token` cookie (see billingApi.js), not localStorage.
    const t = Cookies.get("token")
    return t ? { Authorization: `Bearer ${t}` } : {}
}

function workspaceHeader() {
    const id = localStorage.getItem("activeWorkspaceId")
    return id ? { "X-Workspace-ID": id } : {}
}

function headers(extra = {}) {
    return { ...authHeader(), ...workspaceHeader(), "Content-Type": "application/json", ...extra }
}

export function useBranding() {
    const [branding, setBranding] = useState(null)
    const [domain, setDomain] = useState(null)
    const [emailDomain, setEmailDomain] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchBranding = useCallback(async (workspaceId) => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch(`${API}/${workspaceId}`, { headers: headers({}) })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || "Failed to load")
            setBranding(data.branding)
            setDomain(data.domain)
            setEmailDomain(data.emailDomain)
            return data
        } catch (e) {
            setError(e.message)
            return null
        } finally {
            setLoading(false)
        }
    }, [])

    const updateBranding = useCallback(async (workspaceId, updates) => {
        const res = await fetch(`${API}/${workspaceId}`, {
            method: "PUT",
            headers: headers(),
            body: JSON.stringify(updates)
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to update")
        setBranding(data.branding)
        return data.branding
    }, [])

    const addDomain = useCallback(async (workspaceId, domain, defaultFormId) => {
        const res = await fetch(`${API}/${workspaceId}/domain`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify({ domain, defaultFormId })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to add domain")
        setDomain(data.domain)
        return data
    }, [])

    const verifyDomain = useCallback(async (workspaceId) => {
        const res = await fetch(`${API}/${workspaceId}/domain/verify`, {
            method: "POST",
            headers: headers()
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Verification failed")
        setDomain(data.domain)
        return data
    }, [])

    const removeDomain = useCallback(async (workspaceId) => {
        const res = await fetch(`${API}/${workspaceId}/domain`, {
            method: "DELETE",
            headers: headers()
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to remove domain")
        setDomain(null)
        return data
    }, [])

    const addEmailDomain = useCallback(async (workspaceId, opts) => {
        const res = await fetch(`${API}/${workspaceId}/email-domain`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(opts)
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to add email domain")
        setEmailDomain(data.emailDomain)
        return data
    }, [])

    const verifyEmailDomain = useCallback(async (workspaceId) => {
        const res = await fetch(`${API}/${workspaceId}/email-domain/verify`, {
            method: "POST",
            headers: headers()
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Verification failed")
        setEmailDomain(data.emailDomain)
        return data
    }, [])

    return {
        branding, domain, emailDomain, loading, error,
        fetchBranding, updateBranding,
        addDomain, verifyDomain, removeDomain,
        addEmailDomain, verifyEmailDomain
    }
}
